import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../api/axios";
import Logo from "../components/Logo";
import { useAuth } from "../context/AuthContext";

export default function VerifyEmail() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Students are auto-verified — this page is for teachers only
  useEffect(() => {
    if (user?.role === "student") navigate("/search", { replace: true });
  }, [user, navigate]);
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);
  const [email, setEmail] = useState("");
  const called = useRef(false);

  useEffect(() => {
    if (called.current) return; // prevent React StrictMode double-fire
    called.current = true;
    api.get(`/auth/verify-email/${token}`)
      .then(() => setStatus("success"))
      .catch(err => {
        setMessage(err.response?.data?.message || "Verification failed.");
        setStatus("error");
      });
  }, [token]);

  const handleResend = async () => {
    if (!email.trim()) return;
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email: email.trim().toLowerCase() });
      setResent(true);
    } catch {
      setResent(true); // still show success (backend returns ok regardless)
    }
    setResending(false);
  };

  return (
    <div style={{ minHeight:"100vh", background:"#0a0a0a", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"20px 16px" }}>
      <div style={{ background:"#111111", borderRadius:16, border:"1px solid #1e293b", padding:"36px 24px", width:"100%", maxWidth:420, textAlign:"center" }}>

        <div style={{ display:"flex", justifyContent:"center", marginBottom:20 }}>
          <Logo size={26} />
        </div>

        {status === "loading" && (
          <>
            <div style={{ fontSize:36, marginBottom:14 }}>⏳</div>
            <div style={{ fontSize:17, fontWeight:700, color:"#f1f5f9", marginBottom:8 }}>Verifying your email…</div>
            <div style={{ fontSize:13, color:"#475569" }}>Please wait a moment.</div>
          </>
        )}

        {status === "success" && (
          <>
            <div style={{ fontSize:44, marginBottom:14 }}>✅</div>
            <div style={{ fontSize:18, fontWeight:700, color:"#f1f5f9", marginBottom:10 }}>Email Verified!</div>
            <div style={{ fontSize:13, color:"#64748b", marginBottom:28, lineHeight:1.6 }}>
              Your account is now active. You can log in.
            </div>
            <Link
              to="/login?role=teacher"
              style={{ display:"block", padding:"13px", background:"#f97316", color:"#fff", borderRadius:10, fontSize:15, fontWeight:700, textDecoration:"none" }}
            >
              Login →
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <div style={{ fontSize:44, marginBottom:14 }}>❌</div>
            <div style={{ fontSize:17, fontWeight:700, color:"#f1f5f9", marginBottom:8 }}>Link Invalid or Expired</div>
            <div style={{ fontSize:13, color:"#64748b", marginBottom:24, lineHeight:1.6 }}>
              {message || "This verification link has expired or already been used."}
            </div>

            {!resent ? (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:13, color:"#64748b", marginBottom:10 }}>Enter your email to get a new link:</div>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  style={{ width:"100%", padding:"11px 14px", borderRadius:10, border:"1px solid #1e293b", background:"#0d0d0d", color:"#f1f5f9", fontSize:14, outline:"none", boxSizing:"border-box", marginBottom:10 }}
                />
                <button
                  onClick={handleResend}
                  disabled={resending || !email.trim()}
                  style={{ width:"100%", padding:"12px", background:"#f97316", color:"#fff", border:"none", borderRadius:10, fontSize:14, fontWeight:700, cursor:"pointer", opacity: resending || !email.trim() ? 0.6 : 1 }}
                >
                  {resending ? "Sending…" : "Resend Verification Email"}
                </button>
              </div>
            ) : (
              <div style={{ fontSize:13, color:"#22c55e", marginBottom:20, background:"#22c55e15", padding:"12px 16px", borderRadius:8, border:"1px solid #22c55e30" }}>
                ✓ New verification link sent! Check your inbox.
              </div>
            )}

            <Link to="/login" style={{ fontSize:13, color:"#475569", textDecoration:"none" }}>
              ← Back to Login
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
